import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import React, { FC, ReactNode, useMemo } from 'react';
import { AiOutlineDropbox } from 'react-icons/ai';
import { BsCurrencyDollar } from 'react-icons/bs';
import { FaBox, FaInbox, FaUsers } from 'react-icons/fa';
import { FiShoppingCart } from 'react-icons/fi';
import { IoHomeOutline, IoNewspaperOutline, IoOptionsOutline, IoPricetag } from 'react-icons/io5';
import { MdBusiness, MdOutlineGridView, MdOutlinePrecisionManufacturing } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Hooks/Auth';
import { adminVersion } from '../../Services/api';
import hubLogo from '../../Assets/branding/hub-logo';

interface MenuProps {
  open: boolean;
  onClose: () => void;
}

export interface PermissionsScreens {
  [menu: string]: string;
}

interface HubMenuItem {
  title: string;
  path: string;
  icon: ReactNode;
  permission?: string;
}

interface HubMenuGroup {
  group: string | null;
  items: HubMenuItem[];
}

function isPathActive(pathname: string, path: string): boolean {
  if (path === '/dashboard') {
    return pathname === '/dashboard';
  }

  return pathname === path || pathname.startsWith(`${path}/`);
}

const Menu: FC<MenuProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const hasPermission = (permission: string) => {
    const isHCP = permission.startsWith('menu:hcp') || permission.startsWith('feature:hcp-');
    return user?.permissionsScreens?.includes(`${permission}:manage`)
      || user?.permissionsScreens?.includes('all')
      || (isHCP ? user?.permissionsScreens?.includes('all:hcp') : user?.permissionsScreens?.includes('all:hub'));
  };

  const sections = useMemo<HubMenuGroup[]>(() => [
    {
      group: null,
      items: [
        { title: 'Home', path: '/dashboard', icon: <IoHomeOutline size={20} /> },
      ],
    },
    {
      group: 'Pessoas',
      items: [
        ...(hasPermission('menu:sellers') ? [{ title: 'Vendedores', path: '/sellers-list', icon: <FaUsers size={18} />, permission: 'menu:sellers' }] : []),
        ...(hasPermission('menu:collaborators') ? [{ title: 'Colaboradores', path: '/collaborators-list', icon: <FaUsers size={18} />, permission: 'menu:collaborators' }] : []),
        ...(hasPermission('menu:customers') ? [{ title: 'Clientes', path: '/customers-list', icon: <FaUsers size={18} />, permission: 'menu:customers' }] : []),
      ],
    },
    {
      group: 'Pedidos',
      items: [
        ...(hasPermission('menu:orders') ? [{ title: 'Pedidos', path: '/orders-list', icon: <IoNewspaperOutline size={18} />, permission: 'menu:orders' }] : []),
        ...(hasPermission('menu:replacementOrders') ? [{ title: 'Pedidos Reposição', path: '/orders-replacement-list', icon: <IoNewspaperOutline size={18} />, permission: 'menu:replacementOrders' }] : []),
      ],
    },
    {
      group: 'Catálogo',
      items: [
        ...(hasPermission('menu:products') ? [{ title: 'Produtos', path: '/products-list', icon: <FiShoppingCart size={18} />, permission: 'menu:products' }] : []),
        ...(hasPermission('menu:productMasks') ? [{ title: 'Cadastro de Máscaras', path: '/product-masks', icon: <IoOptionsOutline size={18} />, permission: 'menu:productMasks' }] : []),
        ...(hasPermission('menu:productsServices') ? [{ title: 'Serviços', path: '/services-list', icon: <IoNewspaperOutline size={18} />, permission: 'menu:productsServices' }] : []),
        ...(hasPermission('menu:products') ? [{ title: 'Política de Preços', path: '/price-policies-list', icon: <IoPricetag size={18} />, permission: 'menu:products' }] : []),
      ],
    },
    {
      group: 'Logística',
      items: [
        ...(hasPermission('menu:boxes') ? [{ title: 'Caixas', path: '/box-list', icon: <AiOutlineDropbox size={20} />, permission: 'menu:boxes' }] : []),
        ...(hasPermission('menu:cubages') ? [{ title: 'Cubagem', path: '/cubage-list', icon: <FaBox size={18} />, permission: 'menu:cubages' }] : []),
        ...(hasPermission('menu:stocks') ? [{ title: 'Estoque', path: '/stocks-list', icon: <FaInbox size={18} />, permission: 'menu:stocks' }] : []),
      ],
    },
    {
      group: 'Operação',
      items: [
        ...(hasPermission('menu:production') ? [{ title: 'Produção', path: '/production-list', icon: <MdOutlinePrecisionManufacturing size={20} />, permission: 'menu:production' }] : []),
        ...(hasPermission('menu:financial') ? [{ title: 'Financeiro', path: '/financial-list', icon: <BsCurrencyDollar size={20} />, permission: 'menu:financial' }] : []),
        ...(hasPermission('menu:omieCompanies') ? [{ title: 'Cadastro de Empresas', path: '/omie-companies', icon: <MdBusiness size={20} />, permission: 'menu:omieCompanies' }] : []),
      ],
    },
  ], [user?.permissionsScreens]);

  const visibleSections = sections.filter((section) => section.items.length > 0);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const renderItem = (item: HubMenuItem) => {
    const isActive = isPathActive(location.pathname, item.path);

    return (
      <ListItem key={item.title} disablePadding>
        <ListItemButton
          onClick={() => handleNavigate(item.path)}
          selected={isActive}
          sx={{
            mx: 1.5,
            mb: 0.5,
            borderRadius: '10px',
            px: 1.5,
            py: 0.875,
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: 'rgba(132, 168, 2, 0.14)',
              color: theme.palette.primary.dark,
              '&:hover': { backgroundColor: 'rgba(132, 168, 2, 0.22)' },
              '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
            },
            '&:hover': {
              backgroundColor: 'rgba(132, 168, 2, 0.08)',
              color: theme.palette.primary.main,
            },
            color: isActive ? theme.palette.primary.dark : 'text.secondary',
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isActive ? 700 : 500,
              lineHeight: 1.3,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        width: 300,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
      role="presentation"
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #ffffff 0%, #f7f9f4 100%)',
        }}
      >
        <Box component="img" src={hubLogo} alt="Papello HUB" sx={{ width: 160, height: 'auto', display: 'block', mb: 0.5 }} />
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          HUB Administrativo
        </Typography>
      </Box>

      {hasPermission('menu:hcp') && (
        <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
          <ListItemButton
            onClick={() => handleNavigate('/hcp/dashboard')}
            sx={{
              borderRadius: 2,
              py: 1.25,
              px: 1.5,
              bgcolor: `${theme.palette.primary.main}14`,
              border: `1px solid ${theme.palette.primary.main}40`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: `${theme.palette.primary.main}22`,
                borderColor: `${theme.palette.primary.main}70`,
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <MdOutlineGridView size={20} color="#fff" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontSize={14} fontWeight={800} color="primary.dark" lineHeight={1.3}>
                Acessar HCP
              </Typography>
              <Typography fontSize={11} color="text.secondary" lineHeight={1.3}>
                Gestão de Produção
              </Typography>
            </Box>
          </ListItemButton>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 1.5,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: 3,
          },
        }}
      >
        {visibleSections.map((section, index) => (
          <Box key={section.group ?? `section-${index}`} sx={{ mb: 1.5 }}>
            {section.group && (
              <Typography
                variant="caption"
                sx={{
                  px: 2.5,
                  mb: 0.75,
                  display: 'block',
                  color: 'text.disabled',
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  fontSize: '0.65rem',
                }}
              >
                {section.group}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map(renderItem)}
            </List>
            {index < visibleSections.length - 1 && section.group && (
              <Divider sx={{ mt: 1.5, mx: 2 }} />
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          Versão {adminVersion}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: {
          border: 'none',
          boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
        },
      }}
    >
      {drawerContent}
    </SwipeableDrawer>
  );
};

export { Menu };
